import { Box, Tooltip, TooltipProps, Typography } from '@mui/material';
import { FC, useMemo } from 'react';

import { useTruncatedText } from './useTruncatedText';

export interface TruncatedTextProps {
  items: Array<string>;
  maxLines?: number;
  typographyProps?: React.ComponentProps<typeof Typography>;
  tooltipProps?: Omit<TooltipProps, 'children'>;
  renderItem?: (index: number) => React.ReactNode;
}

export const TruncatedText: FC<TruncatedTextProps> = ({
  items,
  maxLines = 2,
  typographyProps,
  tooltipProps,
  renderItem,
}) => {
  const { ref, containerRef, hiddenCount } = useTruncatedText({ itemsCount: items.length });
  const { title: tooltipTitle, ...restTooltipProps } = tooltipProps ?? {};

  const fullText = useMemo(() => items.join(', '), [items]);
  const title = tooltipTitle ?? fullText;

  return (
    <Tooltip arrow title={hiddenCount > 0 ? title : ''} {...restTooltipProps}>
      <Box ref={ref} style={{ width: '100%' }}>
        <Typography
          ref={containerRef}
          {...typographyProps}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.5,
            ...typographyProps?.sx,
          }}
        >
          {items.map((name, i, array) => (
            <Typography key={`name-${i}`} component="span" {...typographyProps}>
              {renderItem ? renderItem(i) : name}
              {i < array.length - 1 ? ', ' : ''}
            </Typography>
          ))}
          {hiddenCount > 0 && (
            <Typography component="span" {...typographyProps}>
              {` +${hiddenCount}`}
            </Typography>
          )}
        </Typography>
      </Box>
    </Tooltip>
  );
};
